import { ChangeEvent, forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { getRoleType } from "services/TenantSubmitHandler";
import { useAppSelector } from "store/configureStore";
import { FormFieldValidation } from "store/formTemplate/formTemplateTypes";
import { sanitizeForm } from "utils/htmlSanitizer";
import { getDefaultValue } from "utils/renderTemplates";
import { getNestedError } from "utils/splitDot";
import { mapValidations } from "utils/validationMap";

type FieldVariant = "editable" | "readonly" | "disabled";

/**
 * Props for the InputDate component.
 *
 * @property id - Unique identifier for the field, used for accessibility.
 * @property name - Optional name attribute for the field.
 * @property label - Optional label text to display for the field.
 * @property value - Current value of the field (in YYYY-MM-DD format).
 * @property placeholder - Placeholder text to display when the field is empty.
 * @property variant - Visual variant of the field (custom type `FieldVariant`).
 * @property onChange - Callback fired when the field value changes.
 * @property disabled - If true, disables the field.
 * @property error - Error message to display below the field.
 * @property className - Optional CSS class name(s) for custom styling.
 * @property animalIndex - Index for handling multiple animal profiles (used in forms with multiple animals).
 * @property min - Minimum selectable date (in YYYY-MM-DD format).
 * @property max - Maximum selectable date (in YYYY-MM-DD format).
 */
interface DateFieldProps {
  id: string;
  name?: string;
  label?: string;
  value?: string;
  placeholder?: string;
  variant?: FieldVariant;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  error?: string;
  className?: string;
  animalIndex?: number;
  validations?: FormFieldValidation;
  animalType?: string;
  defaultValue?: string;
  clearOnFill?: string[];
  disableForRole?: string[];
  atLeastOneOf?: string;
  min?: string;
  max?: string;
}

/**
 * InputDate is a reusable form component that renders a date picker field with customizable props.
 * It supports editable, readonly, and disabled variants, displays labels and error messages, and forwards refs.
 *
 * @param id - The unique identifier for the input element.
 * @param name - The name attribute for the input element.
 * @param label - Optional label text displayed above the field.
 * @param value - The value of the date field (YYYY-MM-DD format).
 * @param placeholder - Placeholder text for the field.
 * @param variant - Field variant: 'editable', 'readonly', or 'disabled'. Defaults to 'editable'.
 * @param onChange - Callback fired when the field value changes.
 * @param disabled - Whether the field is disabled. Defaults to false.
 * @param error - Error message to display below the field.
 * @param className - Additional CSS classes for custom styling.
 * @param animalIndex - Optional index for animal-related forms.
 * @param min - Minimum selectable date (YYYY-MM-DD format).
 * @param max - Maximum selectable date (YYYY-MM-DD format).
 * @param rest - Additional props spread onto the input element.
 * @param ref - Ref forwarded to the input element.
 *
 * @returns A JSX element containing a styled date input field with label and error support.
 */
const InputDate = forwardRef<HTMLInputElement, DateFieldProps>(
  (
    {
      id,
      name,
      label,
      value = "",
      placeholder = "",
      variant = "editable",
      onChange,
      disabled = false,
      error,
      className = "",
      animalIndex,
      animalType,
      validations,
      defaultValue,
      clearOnFill,
      disableForRole,
      atLeastOneOf,
      min,
      max,
      ...rest
    },
    ref
  ) => {
    const stageOnePetsData = useAppSelector((state) => state.Tenant.tenantPets);

    // Templates can't express a dynamic date, so max="today" resolves to today's date in Sydney (YYYY-MM-DD)
    const resolvedMax =
      max === "today"
        ? new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" })
        : max;

    // The native date placeholder is browser-controlled (lowercase, black), so while the field is
    // empty it is covered by a grey "DD/MM/YYYY" hint. Dates are picked from the calendar only.
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [isEmpty, setIsEmpty] = useState(!(defaultValue || value));
    const showHint = isEmpty;

    // Typing is disabled: clicking (or Enter/Space) opens the native calendar instead.
    const openPicker = (el: HTMLInputElement) => {
      try {
        el.showPicker?.();
      } catch {
        // showPicker throws if the picker is already open or the browser blocks it; ignore
      }
    };
    const setRefs = useCallback(
      (el: HTMLInputElement | null) => {
        inputRef.current = el;
        if (typeof ref === "function") ref(el);
        else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
      },
      [ref]
    );
    // Re-sync after every render: react-hook-form can set the value without firing input events
    useEffect(() => {
      const empty = !inputRef.current?.value;
      if (empty !== isEmpty) setIsEmpty(empty);
    });

    const baseClass = "input-field";
    const fieldClass = [
      baseClass,
      `${baseClass}-${variant}`,
            disabled ? `${baseClass}-disabled` : "",
      error ? `${baseClass}-error` : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    // Access form context for validation and error handling
    const {
      register,
      formState: { errors },
      setValue,
      trigger
    } = useFormContext();

    const errorMessage =
      name && getNestedError(errors, name)?.message
        ? getNestedError(errors, name).message
        : undefined;

    const handleDynamicClear = (e: React.ChangeEvent<HTMLInputElement>, value: string) => {
      const fieldNameEl = e.target.name;
      const [petType, , indexStr] = fieldNameEl.split(".");
      const index = Number(indexStr);

      if (value !== "" && clearOnFill && clearOnFill.length > 0) {
        clearOnFill.forEach((fieldName: string) => {
          const clearFieldName = petType + "." + fieldName + "." + index;
          setValue(clearFieldName, "");
          const inputEl = document.querySelector(`input[name="${clearFieldName}"]`);
          if (inputEl && inputEl instanceof HTMLInputElement) {
            inputEl.value = "";
          }
        });
      }
    };

    const computedDefaultValue = defaultValue === "" ? getDefaultValue(name ?? "", stageOnePetsData ?? []) : defaultValue;

    return (
      <div className="d-flex flex-column gap-10px input-date-field">
        {label && (
          <label
            className="d-block fs-16px fw-normal text-black"
            htmlFor={id}
            dangerouslySetInnerHTML={{ __html: sanitizeForm(label) }}
          />
        )}
        <div className="d-flex flex-column">
          <div className="input-date-wrapper">
          <input
            id={id}
            type="date"
            className={fieldClass}
            placeholder={placeholder}
            readOnly={variant === "readonly"}
            disabled={disabled || variant === "disabled" || disableForRole?.includes(getRoleType())}
            min={min}
            max={resolvedMax}
            {...(name
              ? {
                  ...register(name, mapValidations(validations ? [validations] : [])),
                  defaultValue: computedDefaultValue,
                  onChange: (e) => {
                    onChange(e);
                    handleDynamicClear(e, e.target.value);

                    // If this field has atLeastOneOf relationship, trigger validation on the related field
                    if (atLeastOneOf && name) {
                      const [petType, , indexStr] = name.split(".");
                      const index = Number(indexStr);
                      const relatedFieldName = petType + "." + atLeastOneOf + "." + index;
                      trigger(relatedFieldName);
                    }
                  }
                }
              : { value, onChange })}
            ref={setRefs}
            onInput={(e) => setIsEmpty(!e.currentTarget.value)}
            onClick={(e) => {
              if (variant === "editable" && !disabled) openPicker(e.currentTarget);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openPicker(e.currentTarget);
              } else if (!["Tab", "Escape", "Backspace", "Delete"].includes(e.key) && !e.metaKey && !e.ctrlKey) {
                e.preventDefault(); // no typing or arrow-key stepping; Backspace/Delete still clear
              }
            }}
            onPaste={(e) => e.preventDefault()}
            autoComplete="off"
            {...rest}
          />
          {showHint && !disabled && variant === "editable" && (
            <span className="input-date-hint" aria-hidden="true">
              DD/MM/YYYY
            </span>
          )}
          </div>
          {(errorMessage || error) && (
            <div className="text-red fs-14px">{errorMessage || error}</div>
          )}
        </div>
      </div>
    );
  }
);

export default InputDate;
